import React from 'react';
import PropTypes from 'prop-types';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import RadioGroupPrimitive from './RadioGroupPrimitive';
import RadioButton from './RadioButton';

/**
 * RadioGroup control with no surrounding label and error/validation message
 * functionality for React. RadioGroup will pass its props into the RadioButton children.
 */
export default class RadioGroup extends React.Component {
  static propTypes = {
    /** The aria label that will be applied to the RadioGroup container, and used to auto-generated testIDs on radio children */
    accessibilityLabel: PropTypes.string.isRequired,
    /** Name for the RadioGroup. Must be unique for each RadioGroup. Should be the same as label of parent FormElement. */
    name: PropTypes.string.isRequired,
    /** Callback is called when user interacts with RadioGroup. Selected 'value' is passed to first argument.
     * 'option' object for the selection is passed as the second argument. */
    onUpdate: PropTypes.func,
    /** options is an array of options* that become the individual radio button items
     *
     *
     * *options can take one of a few shapes: the 'default' shape for them is an object with a value (any type)
     * and a label: (what actually gets displayed inside of the drop down), which should be either a string or a
     * render-able function/node, plus optional heading an disabled props.
     * The value prop is the only one that is required in this form, if no label is provided,
     * then the value will determine it
     * Alternatively, an option can be just a string, and not an object,
     * in which case the string will determine the other properties
     */
    options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.any])
            .isRequired,
          label: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.node,
            PropTypes.func
          ]),
          disabled: PropTypes.bool
        }),
        PropTypes.string
      ])
    ).isRequired,
    /** Reference a validation message element via the aria-describedby attribute */
    describedBy: PropTypes.string,
    /** RadioGroup will set all RadioButton instances to the disabled mode. If 'true', `required` will be set to false. */
    disabled: PropTypes.bool,
    /** Sets the RadioGroup into an error state and is passed to each RadioButton instance. */
    hasError: PropTypes.bool,
    /** html attribute */
    id: PropTypes.string,
    /** RadioGroup will display RadioButton instances in a row instead of block (vertically). */
    inline: PropTypes.bool,
    /** Text direction will be set for all RadioButton instances */
    isRtl: PropTypes.bool,
    /** required prop will set accessibility-related input attributes */
    required: PropTypes.bool,
    /** Selected value for the group */
    selected: PropTypes.any,
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string
  };

  static defaultProps = {
    hasError: false,
    inline: false,
    disabled: false,
    isRtl: false,
    selected: undefined
  };

  render() {
    const {
      hasError,
      inline,
      disabled,
      isRtl,
      id,
      onUpdate,
      options,
      name,
      required,
      selected,
      describedBy,
      testID,
      accessibilityLabel,
      // eslint-disable-next-line react/prop-types
      onChange,
      ...restProps
    } = this.props;

    return (
      <ThemeConsumer>
        {theme => (
          <RadioGroupPrimitive
            {...restProps}
            accessibilityLabel={accessibilityLabel}
            id={id}
            role="radiogroup"
            describedBy={describedBy}
            testID={testID}
            isRtl={isRtl}
            theme={theme}
            inline={inline}
          >
            {options.map((rb, i) => {
              if (!(typeof rb === 'string' || typeof rb === 'number')) {
                return (
                  <RadioButton
                    key={`${name}__${i}`}
                    id={id ? `${id}__${name}__${i}` : `${name}__${i}`}
                    name={name}
                    required={required && !disabled}
                    option={{
                      ...rb,
                      disabled: this.checkForDisabled(disabled, rb.disabled),
                      checked: rb.value === selected || false
                    }}
                    testID={testID}
                    inline={inline}
                    hasError={hasError}
                    isRtl={isRtl}
                    onChange={onChange}
                    onUpdate={onUpdate}
                    theme={theme}
                  />
                );
              }
              return (
                <RadioButton
                  key={`${name}__${i}`}
                  id={id ? `${id}__${name}__${i}` : `${name}__${i}`}
                  name={name}
                  required={required && !disabled}
                  option={{
                    value: rb,
                    label: rb,
                    checked: rb === selected || false
                  }}
                  testID={testID}
                  inline={inline}
                  hasError={hasError}
                  isRtl={isRtl}
                  onChange={onChange}
                  onUpdate={onUpdate}
                  theme={theme}
                />
              );
            })}
          </RadioGroupPrimitive>
        )}
      </ThemeConsumer>
    );
  }

  checkForDisabled = (groupDisabled, optionDisabled) => {
    if (groupDisabled) {
      return true;
    } else if (optionDisabled) {
      return true;
    }
    return false;
  };
}
